import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { HiQrcode, HiX } from 'react-icons/hi';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import './Embalagem.css';

const QR_READER_ID = 'embalagem-qr-reader';

const extractShippingCode = (raw) => {
  const text = String(raw ?? '')
    .replace(/[\u200b-\u200d\ufeff]/g, '')
    .trim();
  if (!text) return '';

  let code = '';
  if (/^https?:\/\//i.test(text)) {
    try {
      const url = new URL(text);
      code = (
        url.searchParams.get('codigo_pacote') ||
        url.searchParams.get('pacote') ||
        url.searchParams.get('code') ||
        url.searchParams.get('codigo') ||
        ''
      ).trim();
      if (!code) {
        const last = url.pathname.split('/').filter(Boolean).pop() || '';
        code = decodeURIComponent(last).trim();
      }
    } catch {
      code = text;
    }
  }

  if (!code) {
    const compact = text.replace(/\s+/g, '');
    const match = compact.match(/((?:CX|ML|PL|CT|INV|CLI)[A-Z0-9]+)/i);
    code = (match ? match[1] : compact).trim();
  }

  return code;
};

const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '');

const normalizeBoxNumber = (value) => digitsOnly(value);

const sameCode = (a, b) =>
  String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

const invoiceLabel = (invoice) =>
  invoice?.numero_documento ||
  invoice?.nome_remessa ||
  invoice?.codigo_invoice ||
  (invoice?.id ? `Invoice #${invoice.id}` : '');

const Embalagem = () => {
  const { notifications, removeNotification, showError, showSuccess, showWarning } = useNotification();
  const scannerRef = useRef(null);
  const handledRef = useRef(false);
  const onScanRef = useRef(null);

  const [showScanner, setShowScanner] = useState(false);
  const [scanMode, setScanMode] = useState('qr');
  const [scannerNonce, setScannerNonce] = useState(0);
  const [manualCode, setManualCode] = useState('');
  const [scanError, setScanError] = useState('');
  const [lookingUp, setLookingUp] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState('numero');
  const [pkg, setPkg] = useState(null);
  const [numero, setNumero] = useState('');
  const [nota, setNota] = useState('');
  const [currentBoxes, setCurrentBoxes] = useState([]);
  const [knownBoxes, setKnownBoxes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [invoiceQuery, setInvoiceQuery] = useState('');
  const [invoiceResults, setInvoiceResults] = useState([]);
  const [searchingInvoices, setSearchingInvoices] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const stopScanner = async () => {
    const instance = scannerRef.current;
    scannerRef.current = null;
    if (!instance) return;
    try {
      await instance.stop();
    } catch {
      /* já parado */
    }
    try {
      instance.clear();
    } catch {
      /* ignore */
    }
  };

  const failScan = (message) => {
    setScanError(message);
    setLookingUp(false);
    handledRef.current = false;
    if (scanMode === 'qr') setScannerNonce((n) => n + 1);
  };

  const loadKnownBoxes = async (invoiceId, pacoteId) => {
    if (!invoiceId) return;
    try {
      const json = await apiService.getCaixaFisicaBoard(invoiceId);
      const data = json?.data || json;
      const caixas = Array.isArray(data?.caixas) ? data.caixas : [];
      setKnownBoxes(caixas);
      const row = (data?.pacotes || []).find((item) => Number(item.id) === Number(pacoteId));
      setCurrentBoxes(Array.isArray(row?.caixas_fisicas) ? row.caixas_fisicas : []);
    } catch {
      setKnownBoxes([]);
      setCurrentBoxes([]);
    }
  };

  const openPlacement = async (found) => {
    const invoice = found?.invoice?.id ? found.invoice : null;
    setPkg(found);
    setSelectedInvoice(invoice);
    setInvoiceQuery('');
    setInvoiceResults([]);
    setNumero('');
    setNota('');
    setCurrentBoxes([]);
    setKnownBoxes([]);
    setStep(invoice ? 'numero' : 'invoice');
    setModalOpen(true);
    setShowScanner(false);
    setLookingUp(false);
    setScanError('');
    if (invoice?.id) {
      await loadKnownBoxes(invoice.id, found.id);
    }
  };

  const lookupLabel = async (raw) => {
    const code = extractShippingCode(raw);
    if (!code) {
      failScan('QR lido, mas não foi possível extrair o código da etiqueta.');
      return;
    }
    if (/^cli/i.test(code)) {
      failScan('Esse QR é de retirada. Escaneie a etiqueta de envio da carga.');
      return;
    }
    if (/^inv/i.test(code)) {
      failScan('Esse QR é de invoice. Escaneie a etiqueta de envio da carga.');
      return;
    }

    setLookingUp(true);
    setScanError('');
    try {
      const response = await apiService.getBoxes({ search: code, per_page: 20 });
      const list = normalizeApiData(response);
      const found = (Array.isArray(list) ? list : []).find((item) =>
        sameCode(item?.codigo_pacote || item?.codigo, code)
      );
      if (!found) {
        failScan('Etiqueta não encontrada. Confira o QR da carga.');
        return;
      }
      await openPlacement(found);
    } catch (error) {
      failScan(error.message || 'Não foi possível buscar esta etiqueta.');
    }
  };

  onScanRef.current = (decodedText) => {
    void stopScanner();
    void lookupLabel(decodedText);
  };

  useEffect(() => {
    if (!showScanner || scanMode !== 'qr' || modalOpen) {
      void stopScanner();
      return undefined;
    }

    let cancelled = false;
    handledRef.current = false;

    const start = async () => {
      await stopScanner();
      if (cancelled) return;
      const el = document.getElementById(QR_READER_ID);
      if (!el) return;

      const html5QrCode = new Html5Qrcode(QR_READER_ID);
      scannerRef.current = html5QrCode;
      try {
        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const edge = Math.min(viewfinderWidth, viewfinderHeight);
              const size = Math.max(160, Math.floor(edge * 0.72));
              return { width: size, height: size };
            },
          },
          (decodedText) => {
            if (cancelled || handledRef.current) return;
            handledRef.current = true;
            if (onScanRef.current) onScanRef.current(decodedText);
          },
          () => undefined
        );
      } catch {
        if (!cancelled) {
          setScanError('Câmera indisponível. Digite o código da etiqueta.');
          setScanMode('manual');
        }
      }
    };

    void start();
    return () => {
      cancelled = true;
      void stopScanner();
    };
  }, [showScanner, scanMode, scannerNonce, modalOpen]);

  const startScan = () => {
    setScanError('');
    setManualCode('');
    setScanMode('qr');
    handledRef.current = false;
    setScannerNonce((n) => n + 1);
    setShowScanner(true);
  };

  const resetModal = () => {
    setModalOpen(false);
    setStep('numero');
    setPkg(null);
    setSelectedInvoice(null);
    setInvoiceQuery('');
    setInvoiceResults([]);
    setNumero('');
    setNota('');
    setCurrentBoxes([]);
    setKnownBoxes([]);
  };

  useEffect(() => {
    if (!modalOpen || step !== 'invoice') return undefined;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearchingInvoices(true);
      try {
        const data = await apiService.getInvoicesList({
          search: invoiceQuery.trim() || undefined,
          per_page: 20,
        });
        if (cancelled) return;
        const list = normalizeApiData(data);
        setInvoiceResults(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setInvoiceResults([]);
      } finally {
        if (!cancelled) setSearchingInvoices(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [modalOpen, step, invoiceQuery]);

  const continueToBoxNumber = () => {
    if (!selectedInvoice?.id) {
      showError('Selecione a invoice.');
      return;
    }
    setStep('numero');
    void loadKnownBoxes(selectedInvoice.id, pkg?.id);
  };

  const routesMatchInvoice = (cargo, invoice) => {
    const origemInvoice = String(invoice?.origem || '').trim().toLowerCase();
    const destinoInvoice = String(invoice?.destino || '').trim().toLowerCase();
    if (!origemInvoice || !destinoInvoice) return true;
    const origemCargo = String(cargo?.origem || '').trim().toLowerCase();
    const destinoCargo = String(cargo?.destino || '').trim().toLowerCase();
    return origemCargo === origemInvoice && destinoCargo === destinoInvoice;
  };

  const ensurePackageOnInvoice = async (invoiceId, cargo) => {
    const response = await apiService.getInvoice(invoiceId);
    const invoice = response?.data || response;
    const pacotes = invoice?.pacotes;
    if (!Array.isArray(pacotes)) {
      throw new Error('Não foi possível ler as cargas desta invoice.');
    }
    if (!routesMatchInvoice(cargo, invoice)) {
      throw new Error(
        `A carga tem origem/destino que não correspondem à fatura ("${cargo?.origem || 'não informada'}" → "${cargo?.destino || 'não informado'}").`
      );
    }
    const ids = pacotes.map((item) => Number(item.id)).filter((id) => id > 0);
    if (!ids.includes(Number(cargo.id))) {
      await apiService.updateInvoice(invoiceId, { packages: [...ids, Number(cargo.id)] });
    }
  };

  const closeModal = () => {
    if (saving) return;
    resetModal();
  };

  const placeCargo = async (event) => {
    event.preventDefault();
    if (step === 'invoice') {
      continueToBoxNumber();
      return;
    }

    const invoiceId = pkg?.invoice?.id || selectedInvoice?.id;
    const boxNumber = normalizeBoxNumber(numero);
    if (!pkg || !invoiceId) {
      showError('Selecione a invoice.');
      return;
    }
    if (!boxNumber) {
      showError('Informe o número da caixa.');
      return;
    }

    setSaving(true);
    try {
      if (!pkg?.invoice?.id) {
        await ensurePackageOnInvoice(invoiceId, pkg);
      }

      const json = await apiService.getCaixaFisicaBoard(invoiceId);
      const data = json?.data || json;
      const caixas = Array.isArray(data?.caixas) ? data.caixas : [];
      const target = caixas.find((caixa) => sameCode(caixa.numero, boxNumber));
      const note = String(nota || '').trim();
      let message = `${pkg.codigo_pacote} vinculado à caixa ${boxNumber}.`;

      if (target?.id) {
        const already = (target.pacotes || []).find((item) => Number(item.id) === Number(pkg.id));
        const currentNote = String(already?.observacao || '').trim();
        if (already && note === currentNote) {
          setSaving(false);
          showWarning('Este CX já está nesta caixa física');
          return;
        }
        if (!already) {
          await apiService.attachCaixaFisicaPacotes(target.id, [pkg.id]);
        }
        if (note !== currentNote) {
          await apiService.updateCaixaFisicaPacote(target.id, pkg.id, note);
          if (already) message = 'Observação atualizada';
        }
      } else {
        const created = await apiService.createCaixaFisica({
          invoice_id: invoiceId,
          numero: boxNumber,
          ocupacao: 'exclusivo',
          pacote_ids: [pkg.id],
        });
        const caixaId = created?.data?.id;
        if (caixaId && note) {
          await apiService.updateCaixaFisicaPacote(caixaId, pkg.id, note);
        }
        message = 'Caixa física criada com o CX selecionado';
      }

      setRecent((prev) => [
        {
          id: `${pkg.id}-${Date.now()}`,
          codigo: pkg.codigo_pacote,
          numero: boxNumber,
          nota: note,
          cliente: pkg.cliente?.nome || pkg.nome_destinatario || '',
        },
        ...prev,
      ].slice(0, 8));
      showSuccess(message);
      setSaving(false);
      resetModal();
    } catch (error) {
      setSaving(false);
      showError(error.message || 'Não foi possível colocar a carga na caixa.');
    }
  };

  const cliente = pkg?.cliente?.nome || '';
  const destinatario = pkg?.nome_destinatario || '';
  const remessa = invoiceLabel(pkg?.invoice?.id ? pkg.invoice : selectedInvoice);
  const typedNumber = normalizeBoxNumber(numero);
  const existingBox = knownBoxes.find((caixa) => sameCode(caixa.numero, typedNumber));

  return (
    <div className="emb-page">
      {notifications.map((n) => (
        <Notification
          key={n.id}
          message={n.message}
          type={n.type}
          onClose={() => removeNotification(n.id)}
        />
      ))}

      <div className="emb-card">
        <p className="emb-kicker">Embalagem</p>
        <h1>Etiqueta de envio</h1>
        <p className="emb-lead">
          Escaneie o QR da etiqueta de envio. Se a carga ainda não estiver numa
          remessa, escolha o invoice. Depois informe o número da caixa.
        </p>

        {!showScanner ? (
          <button type="button" className="emb-scan" onClick={startScan}>
            <HiQrcode size={22} />
            Escanear etiqueta
          </button>
        ) : (
          <div className="emb-scan-panel">
            <div className="emb-modes">
              <button
                type="button"
                className={scanMode === 'qr' ? 'is-active' : ''}
                onClick={() => {
                  setScanError('');
                  setScanMode('qr');
                  setScannerNonce((n) => n + 1);
                }}
              >
                Câmera
              </button>
              <button
                type="button"
                className={scanMode === 'manual' ? 'is-active' : ''}
                onClick={() => {
                  void stopScanner();
                  setScanMode('manual');
                }}
              >
                Digitar código
              </button>
            </div>

            {scanMode === 'qr' ? (
              <div className="emb-camera">
                <div id={QR_READER_ID} className="emb-camera__reader" />
                {lookingUp ? <div className="emb-camera__wait">Buscando etiqueta…</div> : null}
              </div>
            ) : (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  handledRef.current = true;
                  void lookupLabel(manualCode);
                }}
              >
                <label className="emb-label" htmlFor="emb-manual-code">
                  Código da etiqueta
                </label>
                <input
                  id="emb-manual-code"
                  className="emb-input"
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder="Ex.: CX475"
                  value={manualCode}
                  onChange={(event) => setManualCode(event.target.value)}
                  autoFocus
                />
                <button type="submit" className="emb-scan" disabled={lookingUp}>
                  {lookingUp ? 'Buscando…' : 'Continuar'}
                </button>
              </form>
            )}

            {scanError ? <p className="emb-error">{scanError}</p> : null}

            <button
              type="button"
              className="emb-cancel"
              onClick={() => {
                void stopScanner();
                setShowScanner(false);
                setScanError('');
                setLookingUp(false);
              }}
            >
              Cancelar
            </button>
          </div>
        )}
      </div>

      {recent.length > 0 ? (
        <section className="emb-recent">
          <h2>Nesta sessão</h2>
          <ul>
            {recent.map((item) => (
              <li key={item.id}>
                <strong>{item.codigo}</strong>
                <span>Caixa {item.numero}</span>
                {item.cliente ? <small>{item.cliente}</small> : null}
                {item.nota ? <em>{item.nota}</em> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {modalOpen && pkg ? (
        <div className="emb-modal">
          <button type="button" className="emb-modal__backdrop" aria-label="Fechar" onClick={closeModal} />
          <form
            className="emb-modal__sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="emb-modal-title"
            onSubmit={placeCargo}
          >
            <header className="emb-modal__head">
              <div>
                <p>Etiqueta lida</p>
                <h2 id="emb-modal-title">{pkg.codigo_pacote}</h2>
              </div>
              <button type="button" className="emb-modal__close" onClick={closeModal} aria-label="Fechar">
                <HiX size={20} />
              </button>
            </header>

            <dl className="emb-meta">
              {cliente ? (
                <div>
                  <dt>Remetente</dt>
                  <dd>{cliente}</dd>
                </div>
              ) : null}
              {destinatario ? (
                <div>
                  <dt>Destinatário</dt>
                  <dd>{destinatario}</dd>
                </div>
              ) : null}
              {remessa ? (
                <div>
                  <dt>Remessa</dt>
                  <dd>{remessa}</dd>
                </div>
              ) : null}
            </dl>

            {step === 'invoice' ? (
              <>
                <label className="emb-label" htmlFor="emb-invoice">
                  Invoice
                </label>
                <input
                  id="emb-invoice"
                  className="emb-input"
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder="Código, nome ou destino"
                  value={invoiceQuery}
                  onChange={(event) => setInvoiceQuery(event.target.value)}
                  autoFocus
                />
                <div className="emb-invoice-list" role="listbox" aria-label="Invoices">
                  {searchingInvoices ? <p className="emb-invoice-empty">Buscando invoices…</p> : null}
                  {!searchingInvoices && invoiceResults.length === 0 ? (
                    <p className="emb-invoice-empty">Nenhuma invoice encontrada</p>
                  ) : null}
                  {invoiceResults.map((invoice) => (
                    <button
                      key={invoice.id}
                      type="button"
                      className={
                        selectedInvoice?.id === invoice.id
                          ? 'emb-invoice is-active'
                          : 'emb-invoice'
                      }
                      onClick={() => setSelectedInvoice(invoice)}
                    >
                      <strong>{invoiceLabel(invoice)}</strong>
                      <span>
                        {[invoice.origem, invoice.destino].filter(Boolean).join(' → ') || 'Sem rota'}
                      </span>
                    </button>
                  ))}
                </div>
                <button type="submit" className="emb-scan" disabled={!selectedInvoice}>
                  Continuar
                </button>
              </>
            ) : (
              <>
                {currentBoxes.length > 0 ? (
                  <p className="emb-hint">
                    Já está na caixa {currentBoxes.map((caixa) => caixa.numero).join(', ')}.
                  </p>
                ) : null}

                <label className="emb-label" htmlFor="emb-numero">
                  Número da caixa
                </label>
                <input
                  id="emb-numero"
                  className="emb-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  placeholder="Ex.: 3"
                  value={numero}
                  onChange={(event) => setNumero(digitsOnly(event.target.value))}
                  autoFocus
                  required
                />
                {typedNumber ? (
                  <p className={existingBox ? 'emb-hint' : 'emb-hint emb-hint--new'}>
                    {existingBox
                      ? `A caixa ${typedNumber} já existe nesta viagem. A carga só será vinculada.`
                      : `A caixa ${typedNumber} ainda não existe nesta viagem. Ela será criada.`}
                  </p>
                ) : null}

                <label className="emb-label" htmlFor="emb-nota">
                  Nota sobre o CX na caixa
                </label>
                <textarea
                  id="emb-nota"
                  className="emb-input emb-note"
                  rows={3}
                  placeholder="Ex.: fundo da caixa, lado da farmácia"
                  value={nota}
                  onChange={(event) => setNota(event.target.value)}
                />

                <button type="submit" className="emb-scan" disabled={saving}>
                  {saving ? 'Salvando…' : 'Colocar na caixa'}
                </button>
              </>
            )}
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default Embalagem;
